import { useState, useEffect } from 'react';
import { browserAdapter } from '~/services/browserAdapter';

/**
 * BrowserAdapter Demo Page
 * 
 * Demonstrates the browserAdapter service in action:
 * - Context detection (iframe vs standalone)
 * - Storage operations (get, set, remove)
 * - Automatic fallback behavior
 * - Performance testing
 */
export default function BrowserAdapterDemoPage() {
  const [storageKey, setStorageKey] = useState('demo_key');
  const [storageValue, setStorageValue] = useState('');
  const [retrievedValue, setRetrievedValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [operationTime, setOperationTime] = useState<number | null>(null);

  // Get context info on mount
  const context = browserAdapter.context;

  useEffect(() => {
    // Test retrieval on mount
    handleGet();
  }, []);

  const handleSet = async () => {
    setIsLoading(true);
    setError(null);
    const start = performance.now();

    try {
      await browserAdapter.storage.set(storageKey, storageValue);
      const end = performance.now();
      setOperationTime(end - start);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Set operation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGet = async () => {
    setIsLoading(true);
    setError(null);
    const start = performance.now();

    try {
      const value = await browserAdapter.storage.get(storageKey);
      setRetrievedValue(value);
      const end = performance.now();
      setOperationTime(end - start);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Get operation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    setIsLoading(true);
    setError(null);
    const start = performance.now();

    try {
      await browserAdapter.storage.remove(storageKey);
      setRetrievedValue(null);
      const end = performance.now();
      setOperationTime(end - start);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove operation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = async () => {
    setIsLoading(true);
    setError(null);
    const start = performance.now();

    try {
      await browserAdapter.storage.clear();
      setRetrievedValue(null);
      const end = performance.now();
      setOperationTime(end - start);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clear operation failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-4xl font-bold mb-2">BrowserAdapter Demo</h1>
      <p className="text-muted-foreground mb-8">
        Test the browserAdapter service in different contexts
      </p>

      {/* Context Information */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Context Detection</h2>
        <div className="grid gap-3">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Running in iframe:</span>
            <span className={`font-mono font-bold ${context.browser.isIframe ? 'text-green-600' : 'text-blue-600'}`}>
              {context.browser.isIframe ? '✓ Yes' : '✗ No (standalone)'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Third-party context:</span>
            <span className={`font-mono font-bold ${context.browser.isThirdPartyContext ? 'text-yellow-600' : 'text-green-600'}`}>
              {context.browser.isThirdPartyContext ? '✓ Yes (restricted)' : '✗ No'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Active adapter:</span>
            <span className="font-mono font-bold text-primary">
              {context.activeAdapter}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Can persist data:</span>
            <span className={`font-mono font-bold ${context.canPersist ? 'text-green-600' : 'text-red-600'}`}>
              {context.canPersist ? '✓ Yes' : '✗ No (session only)'}
            </span>
          </div>
        </div>

        {!context.canPersist && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              ⚠️ <strong>Session-only mode:</strong> Data will be lost when you refresh the page.
              {context.browser.isIframe && ' Ask the parent page to add the storage bridge script for persistence.'}
            </p>
          </div>
        )}

        {context.activeAdapter === 'parent' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">
              ✓ <strong>Parent bridge active:</strong> Storage is persisted via the parent window!
            </p>
          </div>
        )}
      </div>

      {/* Storage Operations */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Storage Operations</h2>

        {/* Set Operation */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Set Value</label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={storageKey}
              onChange={(e) => setStorageKey(e.target.value)}
              placeholder="Key"
              className="flex-1 px-3 py-2 border border-border rounded-md bg-background"
            />
            <input
              type="text"
              value={storageValue}
              onChange={(e) => setStorageValue(e.target.value)}
              placeholder="Value"
              className="flex-1 px-3 py-2 border border-border rounded-md bg-background"
            />
            <button
              onClick={handleSet}
              disabled={isLoading || !storageKey || !storageValue}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Set
            </button>
          </div>
        </div>

        {/* Get Operation */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Get Value</label>
          <div className="flex gap-2 mb-2">
            <button
              onClick={handleGet}
              disabled={isLoading || !storageKey}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Get
            </button>
            <button
              onClick={handleRemove}
              disabled={isLoading || !storageKey}
              className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Remove
            </button>
            <button
              onClick={handleClear}
              disabled={isLoading}
              className="px-4 py-2 border border-border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear All
            </button>
          </div>

          {retrievedValue !== null && (
            <div className="p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground mb-1">Retrieved value:</p>
              <p className="font-mono text-sm break-all">
                {retrievedValue || <span className="text-muted-foreground italic">(empty string)</span>}
              </p>
            </div>
          )}
        </div>

        {/* Performance Info */}
        {operationTime !== null && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-800">
              ⚡ Operation completed in <strong>{operationTime.toFixed(2)}ms</strong>
              {' '}using <strong>{context.activeAdapter}</strong> adapter
            </p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">
              ❌ <strong>Error:</strong> {error}
            </p>
          </div>
        )}
      </div>

      {/* Quick Test Buttons */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Tests</h2>
        <div className="grid gap-2 md:grid-cols-2">
          <button
            onClick={async () => {
              await browserAdapter.storage.set('test_token', 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...');
              handleGet();
            }}
            className="px-4 py-2 bg-card border border-border rounded-md hover:bg-accent"
          >
            Set JWT Token
          </button>
          <button
            onClick={async () => {
              await browserAdapter.storage.set('tenant_context', JSON.stringify({ 
                tenantHandle: 'drivingrangecollective', 
                clubId: 2 
              }));
              handleGet();
            }}
            className="px-4 py-2 bg-card border border-border rounded-md hover:bg-accent"
          >
            Set Tenant Context
          </button>
          <button
            onClick={async () => {
              await browserAdapter.storage.set('booking_draft', JSON.stringify({ 
                date: new Date().toISOString().split('T')[0], 
                timeSlot: '10:00' 
              }));
              handleGet();
            }}
            className="px-4 py-2 bg-card border border-border rounded-md hover:bg-accent"
          >
            Set Booking Draft
          </button>
          <button
            onClick={async () => {
              const keys = ['test_token', 'tenant_context', 'booking_draft'];
              for (const key of keys) {
                const value = await browserAdapter.storage.get(key);
                console.log(`${key}:`, value);
              }
            }}
            className="px-4 py-2 bg-card border border-border rounded-md hover:bg-accent"
          >
            Log All Test Data
          </button>
        </div>
      </div>

      {/* Usage Example */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <h3 className="text-sm font-semibold mb-2">Usage Example:</h3>
        <pre className="text-xs overflow-x-auto">
{`import { browserAdapter } from '~/services/browserAdapter';

// Set value
await browserAdapter.storage.set('access_token', token);

// Get value
const token = await browserAdapter.storage.get('access_token');

// Check context
if (!browserAdapter.context.canPersist) {
  butterToast.info('Session only - data will be lost on refresh');
}`}
        </pre>
      </div>
    </div>
  );
}
